import React, { useState } from 'react';
import {
  Form,
  Input,
  DatePicker,
  Button,
  Table,
  Space,
  message,
} from 'antd';
import {
  SearchOutlined,
  PlusOutlined,
  SendOutlined,
  FileDoneOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  DownloadOutlined,
  MailOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import locale from 'antd/es/date-picker/locale/vi_VN';
import { prescriptionService } from '../../services';
import { Prescription } from '../../types';
import { formatDate } from '../../utils/format';
import { getApiErrorMessage } from '../../utils/error';
import PrescriptionDetailModal from '../../components/PrescriptionDetailModal';
import './PrescriptionManagementPage.css';

const PrescriptionManagementPage: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedPrescriptionId, setSelectedPrescriptionId] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const queryClient = useQueryClient();

  const [filters, setFilters] = useState({
    code: '',
    patientName: '',
    doctorName: '',
    startDate: '',
    endDate: '',
  });

  const { data: prescriptionResponse, isLoading } = useQuery({
    queryKey: ['prescriptions', filters, currentPage, pageSize],
    queryFn: () => prescriptionService.getList({ ...filters, page: currentPage, pageSize }),
  });

  const sendMutation = useMutation({
    mutationFn: (ids: number[]) =>
      ids.length === 1
        ? prescriptionService.syncNational(ids[0])
        : prescriptionService.syncNationalBulk(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prescriptions'] });
      message.success('Gửi đơn thuốc thành công!');
      setSelectedRowKeys([]);
    },
    onError: (error: any) => {
      message.error(getApiErrorMessage(error, 'Gửi đơn thuốc thất bại!'));
    },
  });

  const exportMutation = useMutation({
    mutationFn: (ids: number[]) =>
      ids.length === 1
        ? prescriptionService.exportInvoice(ids[0])
        : prescriptionService.exportInvoiceBulk(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prescriptions'] });
      message.success('Xuất hóa đơn thành công!');
      setSelectedRowKeys([]);
    },
    onError: (error: any) => {
      message.error(getApiErrorMessage(error, 'Xuất hóa đơn thất bại!'));
    },
  });

  const onSearch = (values: any) => {
    setFilters({
      code: values.code || '',
      patientName: values.patientName || '',
      doctorName: values.doctorName || '',
      startDate: values.startDate ? values.startDate.format('YYYY-MM-DD') : '',
      endDate: values.endDate ? values.endDate.format('YYYY-MM-DD') : '',
    });
  };

  const handleViewDetail = (id: number) => {
    setSelectedPrescriptionId(id);
    setDetailModalVisible(true);
  };

  const handleSendAll = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Vui lòng chọn ít nhất một đơn thuốc!');
      return;
    }
    sendMutation.mutate(selectedRowKeys as number[]);
  };

  const handleExportAll = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Vui lòng chọn ít nhất một đơn thuốc!');
      return;
    }
    exportMutation.mutate(selectedRowKeys as number[]);
  };

  const handleSendSingle = (id: number) => {
    sendMutation.mutate([id]);
  };

  const handleExportSingle = (id: number) => {
    exportMutation.mutate([id]);
  };

  const formatDateOnly = (dateString: string) => {
    if (!dateString) return '';
    return dayjs(dateString).format('DD/MM/YYYY');
  };

  const columns = [
    {
      title: 'STT',
      dataIndex: 'index',
      key: 'index',
      width: 60,
    },
    {
      title: 'Mã đơn thuốc',
      dataIndex: 'code',
      key: 'code',
      render: (text: string, record: Prescription) => (
        <Button
          type="link"
          onClick={() => handleViewDetail(record.id)}
          className="prescription-code-link"
        >
          {text}
        </Button>
      ),
    },
    {
      title: 'Bác sĩ',
      dataIndex: 'doctor_name',
      key: 'doctor_name',
    },
    {
      title: 'Tên bệnh nhân',
      dataIndex: 'patient_name',
      key: 'patient_name',
    },
    {
      title: 'Chẩn đoán',
      dataIndex: 'diagnosis',
      key: 'diagnosis',
    },
    {
      title: 'Ngày tạo đơn',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => formatDateOnly(date),
    },
    {
      title: 'Gửi đơn thuốc',
      key: 'dtqg_status',
      render: (_: any, record: Prescription) => (
        <div className="status-cell">
          {record.dtqg_status === 'sent' ? (
            <Space className="status-sent">
              <EyeOutlined />
              <span>Đã gửi ĐTQG</span>
            </Space>
          ) : (
            <Button
              type="primary"
              size="small"
              icon={<SendOutlined />}
              onClick={() => handleSendSingle(record.id)}
              className="btn-send-dtqg"
            >
              Gửi ĐTQG
            </Button>
          )}
        </div>
      ),
    },
    {
      title: 'Xuất hóa đơn',
      key: 'invoice_status',
      render: (_: any, record: Prescription) => (
        <div className="status-cell">
          {record.invoice_status === 'exported' ? (
            <Space className="status-sent">
              <EyeOutlined />
              <span>Đã xuất HĐ</span>
            </Space>
          ) : (
            <Button
              type="primary"
              size="small"
              icon={<SendOutlined />}
              onClick={() => handleExportSingle(record.id)}
              className="btn-export-invoice"
            >
              Xuất HĐ
            </Button>
          )}
        </div>
      ),
    },
    {
      title: 'Tùy chọn',
      key: 'actions',
      render: (_: any, record: Prescription) => (
        <Space className="options-cell">
          {record.dtqg_status === 'sent' && record.invoice_status === 'exported' ? (
            <>
              <Button type="text" icon={<DownloadOutlined />} className="option-icon" />
              <Button type="text" icon={<MailOutlined />} className="option-icon" />
            </>
          ) : (
            <>
              <Button type="text" icon={<EditOutlined />} className="option-icon option-edit" />
              <Button type="text" icon={<DeleteOutlined />} className="option-icon option-delete" />
            </>
          )}
        </Space>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys: React.Key[]) => {
      setSelectedRowKeys(keys);
    },
  };

  const prescriptions = prescriptionResponse?.data || [];
  const tableData = prescriptions.map((p, index) => ({
    ...p,
    index: (currentPage - 1) * pageSize + index + 1,
    key: p.id,
  }));

  return (
    <div className="prescription-management-page">
      <h1 className="page-title">Quản lý đơn thuốc ngày {formatDate(dayjs().format('YYYY-MM-DD'))}</h1>

      <div className="filter-row">
        <Form form={form} layout="inline" onFinish={onSearch} className="search-form">
          <Form.Item name="code">
            <Input placeholder="Mã đơn thuốc" style={{ width: 160 }} />
          </Form.Item>
          <Form.Item name="patientName">
            <Input placeholder="Tên bệnh nhân" style={{ width: 160 }} />
          </Form.Item>
          <Form.Item name="doctorName">
            <Input placeholder="Bác Sĩ" style={{ width: 160 }} />
          </Form.Item>
          <Form.Item name="startDate">
            <DatePicker placeholder="Từ ngày" format="DD/MM/YYYY" locale={locale} />
          </Form.Item>
          <Form.Item name="endDate">
            <DatePicker placeholder="Đến ngày" format="DD/MM/YYYY" locale={locale} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
              Tìm kiếm
            </Button>
          </Form.Item>
        </Form>
        <Space className="top-action-buttons">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/facility/prescriptions/import')}
          >
            Import dữ liệu excel
          </Button>
          {/* <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/facility/prescriptions/add')}
          >
            Nhập đơn thuốc thủ công
          </Button> */}
        </Space>
      </div>

      <Table
        rowSelection={rowSelection}
        columns={columns}
        dataSource={tableData}
        loading={isLoading}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: prescriptionResponse?.meta?.count || 0,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          showTotal: (total) => `Tổng ${total} đơn thuốc`,
          onChange: (page, size) => {
            setCurrentPage(page);
            setPageSize(size);
          },
        }}
      />

      <div className="bulk-actions">
        <Button
          type="primary"
          icon={<SendOutlined />}
          onClick={handleSendAll}
          loading={sendMutation.isPending}
          className="bulk-action-btn"
        >
          Gửi toàn bộ ĐTQG
        </Button>
        <Button
          type="primary"
          icon={<FileDoneOutlined />}
          onClick={handleExportAll}
          loading={exportMutation.isPending}
          className="bulk-action-btn"
        >
          Xuất toàn bộ Hóa đơn
        </Button>
      </div>

      <PrescriptionDetailModal
        visible={detailModalVisible}
        prescriptionId={selectedPrescriptionId}
        onClose={() => {
          setDetailModalVisible(false);
          setSelectedPrescriptionId(null);
        }}
      />
    </div>
  );
};

export default PrescriptionManagementPage;

